import {Component, ElementRef, Input, ViewChild} from '@angular/core';
import {TranslateService} from '@ox/translate';
import {selectFeatureFlag, TrackingService} from '@ox/data';
import {AdobeDataLayerBaseCall, ExpandCollection, GfaText, RecallPayload, UIFeatureFlag, WarrantyPayload} from '@ox/typings';
import {Subject, takeUntil, takeWhile, tap} from 'rxjs';
import {AppConfigService, AutoDestroy} from '@ox/utils';
import {RecallsGfasService} from '@ox/recalls';
import {Store} from '@ngrx/store';
import {first} from 'lodash';

interface TrackAttr {
  prop10: string;
  evar33?: string;
  prop15?: string;
}

@Component({
  selector: 'webu-recall-center-search-results',
  templateUrl: './search-results-webu.component.html'
})
export class SearchResultsWebUComponent {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  showToolTip = false;
  currentPageName = '';
  isPOSH: boolean;
  warrantyPageUrl: string;
  hideWarranties:boolean = this.appConfig.appSettings.moduleConfig.hideWarranties ? this.appConfig.appSettings.moduleConfig.hideWarranties : false;
  private _warrantyBrandCmsID: string;
  vehicleMake$ = this.recallsGfas.vehicleMake$.pipe(
    takeUntil(this.destroy$),
    tap(make => {
      if(!make) {
        this.isPOSH = true;
        return;
      }
      this.isPoshCheck(make);
      this.warrantyBrandCmsID = make;
    })
  ).subscribe();
  @ViewChild('faqSection') public faqSection : ElementRef;
  @Input() recallResults: RecallPayload[];
  @Input() completeRecallResults: RecallPayload[];
  @Input() warrantyResults: { expired: WarrantyPayload[]; active: WarrantyPayload[]; };
  @Input() warranty: WarrantyPayload;
  @Input() loading: boolean;
  @Input() recallSearchCompleted: boolean;
  @Input() warrantySearchCompleted: boolean;
  @Input() mmyFound: boolean;
  @Input() vehicleYear: string;
  @Input() vehicleMake: string;
  @Input() vehicleModel: string;
  @Input() searchedVin: string;
  @Input() refreshDate: string;
  @Input() noResultsFoundFaq: ExpandCollection[];
  @Input() nhtsaRecallExists: boolean;
  @Input() nonNhtsaRecallExists: boolean;
  @Input() foundResultsFaq: ExpandCollection[];
  @Input() recall: RecallPayload | undefined = undefined;
  @Input() recallCenterWhatNowSecondParagraph: string | undefined;
  @Input() isAnotherSearch: boolean;
  @Input() landingFaq: ExpandCollection[];
  @Input() vinAttributes: { pattern: RegExp, maxlength: number };
  @Input() warrantyError: boolean;
  @Input() recallsError: boolean;
  private dataLayerV2Enabled$ = this.store.select(selectFeatureFlag({id: UIFeatureFlag.DATALAYER_V2, group: 'UI'}))
    .pipe(
      takeWhile((val) => !val, true)
    );

  //takes a generic object and returns the data that is in a property of the passed object
  //Focus on this part obj?.[item] || ''
  // obj?: Saying "if obj is defined"
  // [item]: Grabbing something from the obj object using item (which is a string or number)

  constructor(
    private translate: TranslateService,
    private trackingService: TrackingService,
    private recallsGfas: RecallsGfasService,
    private appConfig: AppConfigService,
    private store: Store
  ) {
  }

  // || '' if nothing is found in obj using the key item, then default to an empty string \/
  public gfas = (obj: { [x: string]: any; } | GfaText | undefined, item: string) => (obj as any)?.[item] || '';

  public getLang (gfaTexts: GfaText[]) {
    const gfaLang = (item: {langCode: string;}) => item.langCode === this.appConfig.bml.lang.toUpperCase();
    const gfasTextNew = (Array.isArray(gfaTexts) && gfaTexts.length <= 0) ? undefined :
    (gfaTexts.length === 1) ?
    gfaTexts[0] :
    gfaTexts.some(gfaLang) ?
    gfaTexts.find(gfaLang) :
    gfaTexts.filter(
      (item) => item.langCode.toLowerCase().match(this.appConfig.bml.lang.toLowerCase()) )[0];

    return [gfasTextNew];
  }

  public isHidden(obj: Array<any>, tag: string): boolean {
    let isHidden = false;
    for (const textObj of obj) {
      if (!textObj[tag]) {
        isHidden = true;
      } else {
        isHidden = false;
      }
    }
    return isHidden;
  }

  public vinStatusDetailCheck (detailCode : string) {
    let detailCheck = false;
    if(detailCode === '97' || detailCode === '98'){
      detailCheck = true;
      return detailCheck;
    }
    return detailCheck;
  }

  public vinDetailLang (vinStatusDesc : Array<any>) {
      if(vinStatusDesc.length > 1){
        for(let i = 0; i < vinStatusDesc.length; i++){
          if(vinStatusDesc[i].langCode.toUpperCase() === this.appConfig.bml.lang.toUpperCase()){
            return vinStatusDesc[i].statusDesc;
          }
        }
      }
      else{
        return vinStatusDesc[0].statusDesc;
      }
  }

  public fireCustomTracking(): void {
    const id = 'recallCenter_search_button';
    this.translate.get(id).pipe(takeWhile(res => res !== id),
      tap((data: string) => this.onSuccess(data))).subscribe();
  }

  triggerToolTip() {
    this.showToolTip = !this.showToolTip;
  }

  private onSuccess = (response: string) => {
    this.currentPageName = this.trackingService.getPageName();
    if (response) {
      const trackLink = 'recalls:' + response;
      const attributes = {} as TrackAttr;
      attributes.prop10 = 'recalls:' + this.currentPageName + ':hyperlink:click:' + response;
      attributes.prop15 = response;
      attributes.evar33 = 'recalls:' + this.currentPageName + ':hyperlink:click:' + response;
      this.trackingService.setProps(attributes);
      this.trackingService.setEvars(attributes);
      this.trackingService.fireTracking(true, 'o', trackLink);
      //response.data ?? response;
    }
  };

  public navigateToSection() {
    this.faqSection.nativeElement.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'end'});
  }

  public isPoshCheck(make: string): boolean {
    return (this.isPOSH = !make.toLowerCase().match(/buick|cadillac|chevrolet|gmc/));
  }

  get warrantyBrandCmsID() {
    return this._warrantyBrandCmsID;
  }

  set warrantyBrandCmsID(value:string){
    this._warrantyBrandCmsID = `${value.toLowerCase()}_warranty_brand_url`;
  }

  trackSearch(payload: RecallPayload | WarrantyPayload | undefined) {
    const resultsCount = this.recallResults.length + this.completeRecallResults.length;
    this.dataLayerV2Enabled$.subscribe((dataLayerV2Enabled) => {
      if (dataLayerV2Enabled === 'true') {
        const resultDescription = first((payload as RecallPayload).gfaTexts)?.subject ?? (payload as WarrantyPayload)?.description;
        const search = {
          search : {
            term: this.searchedVin,
            resultCount: resultsCount,
            resultClicked: resultDescription,
            facetsUsed: [`${this.vehicleYear} ${this.vehicleMake} ${this.vehicleModel}`],
            filtersUsed: []
          }
        };
        AdobeDataLayerBaseCall.setGmUnifiedProperty(search, window);
      }
    });
  }
}
